import { useEffect, useRef, useState } from 'react'

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'wheel', 'touchstart', 'keydown'] as const

/** How often the elapsed-since-activity check runs. Coarse on purpose. */
const IDLE_TICK_MS = 15_000

/**
 * True once the user has not touched the app for `timeoutMs`.
 *
 * This exists for the webview left open on a desk for the rest of the day:
 * `visibilitychange` only covers the app being backgrounded, not a phone lying
 * face-up on a table with the map still on screen.
 *
 * Activity only stamps a ref — `pointermove` fires at frame rate, so it must
 * never touch state or reschedule a timer. A single coarse interval turns those
 * stamps into the one state change that matters.
 */
export function useDocumentIdle(timeoutMs: number): boolean {
  const [isIdle, setIsIdle] = useState(false)
  // Stamped on mount, not at render time: reading the clock during render is
  // impure and would make the first idle window depend on when React happened
  // to render.
  const lastActivityAtRef = useRef(0)

  useEffect(() => {
    lastActivityAtRef.current = Date.now()

    const markActive = () => {
      lastActivityAtRef.current = Date.now()
      setIsIdle((current) => (current ? false : current))
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') markActive()
    }

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, markActive, { passive: true })
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    const intervalId = window.setInterval(
      () => {
        const isNowIdle = Date.now() - lastActivityAtRef.current >= timeoutMs
        setIsIdle((current) => (current === isNowIdle ? current : isNowIdle))
      },
      Math.min(IDLE_TICK_MS, timeoutMs)
    )

    return () => {
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, markActive)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.clearInterval(intervalId)
    }
  }, [timeoutMs])

  return isIdle
}
